import { DEFAULT_RANDOM_SEED, normalizeRandomSeed } from './DeterministicRandom.js';

export const SIMULATION_SESSION_VERSION = 1;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value;
}

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function assertTimeScale(value) {
  if (!Number.isFinite(value) || value < 0 || value > 20) {
    throw new RangeError('timeScale must be between 0 and 20');
  }
  return value;
}

export function resetSimulationSession({
  clock,
  random,
  input,
  inputReplay,
  simulationSnapshots,
  entityWorld,
  traversalWorld,
  snakeState,
  wildlifeSystem,
  huntingSystem,
  storySystem,
  multiplayerState = null,
}, {
  seed = DEFAULT_RANDOM_SEED,
  paused = true,
  timeScale = 1,
  timeOfDay = 'day',
  weather = 'clear',
} = {}) {
  assertMethod(clock, 'reset', 'clock');
  assertMethod(random, 'setSeed', 'random');
  assertMethod(input, 'reset', 'input');
  assertMethod(inputReplay, 'reset', 'inputReplay');
  assertMethod(simulationSnapshots, 'reset', 'simulationSnapshots');
  assertMethod(entityWorld, 'reset', 'entityWorld');
  assertMethod(traversalWorld, 'reset', 'traversalWorld');
  assertMethod(snakeState, 'reset', 'snakeState');
  assertMethod(wildlifeSystem, 'reset', 'wildlifeSystem');
  assertMethod(huntingSystem, 'reset', 'huntingSystem');
  assertMethod(storySystem, 'reset', 'storySystem');
  if (multiplayerState !== null) assertMethod(multiplayerState, 'reset', 'multiplayerState');
  const normalizedSeed = normalizeRandomSeed(seed);
  assertTimeScale(timeScale);
  assertNonEmptyString(timeOfDay, 'timeOfDay');
  assertNonEmptyString(weather, 'weather');

  const clockState = clock.reset({
    simulationTime: 0,
    timeScale,
    paused,
  });
  const randomState = random.setSeed(normalizedSeed);
  const inputState = input.reset();
  const replayState = inputReplay.reset();
  const snapshotState = simulationSnapshots.reset();
  const multiplayerStateStatus = multiplayerState?.reset() ?? null;
  const storyState = storySystem.reset();
  const huntingState = huntingSystem.reset();
  const wildlifeState = wildlifeSystem.reset({ removeEntities: false });
  const snakeStateStatus = snakeState.reset({ removeEntity: false });
  const traversalState = traversalWorld.reset();
  const entityWorldState = entityWorld.reset();

  const runtime = {
    activeScenario: null,
    timeOfDay,
    weather,
    simulationSteps: 0,
    simulationStep: null,
    simulationSnapshots: clone(snapshotState),
    entityWorld: clone(entityWorldState),
    traversalWorld: clone(traversalState),
    snakeState: clone(snakeStateStatus),
    wildlifeSystem: clone(wildlifeState),
    huntingSystem: clone(huntingState),
    storySystem: clone(storyState),
    random: clone(randomState),
    input: clone(inputState),
    inputReplay: clone(replayState),
  };
  if (multiplayerStateStatus !== null) runtime.multiplayerState = clone(multiplayerStateStatus);

  return deepFreeze({
    version: SIMULATION_SESSION_VERSION,
    clock: clone(clockState),
    runtime,
  });
}
